// FirmDetail.js
import React, { useEffect, useState } from "react";
import {
  MapPin,
  Globe,
  Users,
  Phone,
  Mail,
  Star,
  Calendar,
  Award,
  Building2,
  ArrowLeft,
} from "lucide-react";
import "./firmDetail.css"; // You'll need to create this CSS file
import { useParams, useNavigate } from "react-router-dom";
import { getPracticeAreaLabel } from "../../helpers/stringHelper";
import SearchHeader from "../searchResults/searchHeader";
import ClientLoginPopup from "../../home/new/components/clientLoginPopup";
import NarrowDropdown2 from "../../components/input/new/narrowDropdown2";

const FirmDetail = () => {
  // Use prop or default for demo
  const { id } = useParams();

  console.log("The firm id to use is ", id);
  const firmIdToUse = id;
  const navigate = useNavigate();
  // State management
  const [firm, setFirm] = useState(null);
  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedPracticeArea, setSelectedPracticeArea] = useState("all");
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  const openLoginPopup = () => setShowLoginPopup(true);

  // Close Login Popup function
  const closeLoginPopup = () => setShowLoginPopup(false);

  // Mock data for demonstration - replace with actual API calls
  useEffect(() => {
    const fetchFirmData = async () => {
      try {
        setLoading(true);

        // Fetch firm details from API
        const firmResponse = await fetch(
          `/api/attorney/webapp/fetchFirmDetails?firm_id=${firmIdToUse}`
        );

        if (!firmResponse.ok) {
          throw new Error(`HTTP error! status: ${firmResponse.status}`);
        }

        const firmData = await firmResponse.json();

        if (firmData.error) {
          throw new Error(firmData.error);
        }

        // Fetch lawyers from API
        const lawyersResponse = await fetch(
          `/api/attorney/fetchFirmMembers?firm_id=${firmIdToUse}`
        );

        if (!lawyersResponse.ok) {
          throw new Error(`HTTP error! status: ${lawyersResponse.status}`);
        }

        const lawyersData = await lawyersResponse.json();

        if (lawyersData.error) {
          throw new Error(lawyersData.error);
        }

        setFirm(firmData.firm);
        setLawyers(lawyersData.lawyers || []);
      } catch (err) {
        setError("Failed to load firm details");
        console.error("Error fetching firm data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (firmIdToUse) {
      fetchFirmData();
    }
  }, [firmIdToUse]);

  const handleLawyerClick = (lawyer) => {
    // For demo purposes, just log the lawyer ID
    navigate(`/lawyer-detail/${lawyer.user_id}`, {
      state: { lawyer },
    });

    // In your actual app: navigate(`/lawyer/${lawyerId}`);
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        size={16}
        className={index < Math.floor(rating) ? "star-filled" : "star-empty"}
      />
    ));
  };

  const getPracticeAreaColor = (areaLabel) => {
    const colorMap = {
      red: [
        "Criminal Law",
        "White Collar Crime",
        "Drug Offenses",
        "DUI/DWI",
        "Violent Crimes",
        "Juvenile Delinquency",
        "Federal Crimes",
        "Sex Crimes",
      ],
      blue: [
        "Corporate Law",
        "Mergers & Acquisitions",
        "Corporate Governance",
        "Securities Law",
        "Commercial Contracts",
        "Regulatory Compliance",
        "Corporate Litigation",
      ],
      green: [
        "Family Law",
        "Divorce",
        "Child Custody & Visitation",
        "Child Support",
        "Adoption",
        "Prenuptial & Postnuptial Agreements",
        "Domestic Violence",
        "Guardianship & Conservatorship",
      ],
      orange: [
        "Personal Injury Law",
        "Motor Vehicle Accidents",
        "Slip and Fall",
        "Product Liability",
        "Workplace Accidents",
        "Premises Liability",
        "Catastrophic & Brain Injuries",
      ],
      purple: [
        "Real Estate Law",
        "Residential Transactions",
        "Commercial Transactions",
        "Landlord-Tenant Disputes",
        "Zoning & Land Use",
        "Property Development & Planning",
        "Title, Escrow & Foreclosure",
      ],
      gray: [], // fallback
    };

    for (const [color, labels] of Object.entries(colorMap)) {
      if (labels.includes(areaLabel)) {
        return `practice-area-${color}`;
      }
    }

    return "practice-area-gray";
  };

  const filteredLawyers =
    selectedPracticeArea === "all"
      ? lawyers
      : lawyers.filter((lawyer) =>
          lawyer.practice_areas.some((area) =>
            area.toLowerCase().includes(selectedPracticeArea.toLowerCase())
          )
        );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading firm details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-content">
          <p className="error-message">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="error-button"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!firm) {
    return (
      <div className="not-found-container">
        <p className="not-found-text">Firm not found</p>
      </div>
    );
  }

  return (
    <div className="firm-detail-container">
      <SearchHeader
        openLoginPopup={openLoginPopup}
        searchBar={false}
        headerColor="search-header-gray"
      />
      {/* Header Banner */}
      <div className="header-banner">
        <div className="header-content">
          <div className="header-main">
            <div className="firm-info-section">
              <ArrowLeft
                onClick={() => {
                  navigate(-1);
                }}
                style={{ marginRight: "30px" }}
              />
              <div className="logo-container">
                {firm.logo ? (
                  <img
                    src={firm.logo}
                    alt={`${firm.name} logo`}
                    className="firm-logo"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <div
                  className="firm-logo-fallback"
                  style={{ display: firm.logo ? "none" : "flex" }}
                >
                  {firm.name.charAt(0)}
                </div>
              </div>
              <div className="firm-details">
                <h1 className="firm-name">{firm.name}</h1>
                <div className="firm-meta">
                  <div className="meta-item">
                    <MapPin size={16} />
                    <span>{firm.location}</span>
                  </div>
                  <div className="meta-item">
                    <Users size={16} />
                    <span>{firm.firm_size} employees</span>
                  </div>
                  {firm.website && (
                    <div className="meta-item">
                      <Globe size={16} />
                      <a
                        href={`https://${firm.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="website-link"
                      >
                        {firm.website}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="rating-section">
              <div className="stars-container">
                {renderStars(firm.averageRating)}
                <span className="rating-number">{firm.averageRating}</span>
              </div>
              <p className="review-count">{firm.totalReviews} reviews</p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="nav-tabs-container">
        <div className="nav-tabs-content">
          <nav className="nav-tabs">
            {[
              { id: "overview", label: "Overview" },
              { id: "lawyers", label: "Lawyers" },
              { id: "practice-areas", label: "Practice Areas" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`nav-tab ${
                  activeTab === tab.id ? "nav-tab-active" : ""
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {activeTab === "overview" && (
          <div className="overview-grid">
            {/* Main Content */}
            <div className="overview-main">
              <div className="card">
                <h2 className="card-title">About {firm.name}</h2>
                <p className="firm-description">{firm.description}</p>
                <div className="firm-stats-grid">
                  <div>
                    <strong className="stat-label">Founded:</strong>
                    <p className="stat-value">{firm.founded}</p>
                  </div>
                  <div>
                    <strong className="stat-label">Total Lawyers:</strong>
                    <p className="stat-value">{firm.totalLawyers}</p>
                  </div>
                </div>
              </div>

              <div className="card">
                <h2 className="card-title">Practice Areas</h2>
                <div className="practice-areas-list">
                  {firm.practice_areas.map((area, index) => (
                    <span
                      key={index}
                      className={`practice-area-badge ${getPracticeAreaColor(
                        getPracticeAreaLabel(area)
                      )}`}
                    >
                      {getPracticeAreaLabel(area)}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="overview-sidebar">
              <div className="card">
                <h3 className="sidebar-title">Firm Statistics</h3>
                <div className="sidebar-stats">
                  <div className="sidebar-stat">
                    <div className="sidebar-stat-icon">
                      <Users className="icon-blue" size={20} />
                      <span>Total Lawyers</span>
                    </div>
                    <span className="sidebar-stat-value">
                      {firm.totalLawyers}
                    </span>
                  </div>
                  <div className="sidebar-stat">
                    <div className="sidebar-stat-icon">
                      <Star className="icon-yellow" size={20} />
                      <span>Average Rating</span>
                    </div>
                    <span className="sidebar-stat-value">
                      {firm.averageRating}
                    </span>
                  </div>
                  <div className="sidebar-stat">
                    <div className="sidebar-stat-icon">
                      <Building2 className="icon-green" size={20} />
                      <span>Founded</span>
                    </div>
                    <span className="sidebar-stat-value">{firm.founded}</span>
                  </div>
                </div>
              </div>

              <div className="card">
                <h3 className="sidebar-title">Contact Information</h3>
                <div className="contact-info">
                  <div className="contact-item">
                    <MapPin className="contact-icon" size={18} />
                    <span>{firm.location}</span>
                  </div>
                  {firm.website && (
                    <div className="contact-item">
                      <Globe className="contact-icon" size={18} />
                      <a
                        href={`https://${firm.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="website-link"
                      >
                        {firm.website}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "lawyers" && (
          <div>
            {/* Filter Controls */}
            <div className="filter-container">
              <div className="filter-content">
                <NarrowDropdown2
                  title="Filter by Practice Area"
                  placeholder="All Practice Areas"
                  items={[
                    { label: "All Practice Areas", value: "all" },
                    ...firm.practice_areas.map((area) => ({
                      label: getPracticeAreaLabel(area),
                      value: area,
                    })),
                  ]}
                  value={selectedPracticeArea}
                  onChange={(value) => setSelectedPracticeArea(value)}
                />
                <span className="filter-count">
                  Showing {filteredLawyers.length} of {lawyers.length} lawyers
                </span>
              </div>
            </div>

            {/* Lawyers Grid */}
            <div className="lawyers-grid">
              {filteredLawyers.map((lawyer) => (
                <div
                  key={lawyer.id}
                  onClick={() => handleLawyerClick(lawyer)}
                  className="firm-lawyer-card"
                >
                  <div className="lawyer-header-firm">
                    {lawyer.profile_photo_url ? (
                      <img
                        src={lawyer.profile_photo_url}
                        alt={lawyer.first_name}
                        className="lawyer-photo"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className="lawyer-photo-fallback"
                      style={{
                        display: lawyer.profile_photo_url ? "none" : "flex",
                      }}
                    >
                      {lawyer.first_name.charAt(0)}
                      {lawyer.last_name.charAt(0)}
                    </div>
                    <div className="lawyer-info">
                      <h3 className="lawyer-name">
                        {lawyer.first_name + " " + lawyer.last_name}
                      </h3>
                      <p className="lawyer-role">{lawyer.role}</p>
                      <div className="lawyer-rating">
                        {renderStars(lawyer.average_rating)}
                        <span className="rating-text">
                          {lawyer.average_rating} ({lawyer.number_of_reviews}{" "}
                          reviews)
                        </span>
                      </div>
                      <p className="lawyer-experience">
                        {lawyer.years_of_experience} years experience
                      </p>
                    </div>
                  </div>

                  <div className="lawyer-practice-areas">
                    {lawyer.practice_areas.slice(0, 3).map((area, index) => (
                      <span
                        key={index}
                        className={`practice-area-small ${getPracticeAreaColor(
                          getPracticeAreaLabel(area)
                        )}`}
                      >
                        {getPracticeAreaLabel(area)}
                      </span>
                    ))}
                    {lawyer.practice_areas.length > 3 && (
                      <span className="practice-area-small practice-area-gray">
                        +{lawyer.practice_areas.length - 3} more
                      </span>
                    )}
                  </div>

                  <div className="lawyer-footer">
                    <span>Languages: {lawyer.languages.join(", ")}</span>
                    <Calendar size={16} />
                  </div>
                </div>
              ))}
            </div>

            {filteredLawyers.length === 0 && (
              <div className="no-results">
                <p>No lawyers found for the selected practice area.</p>
              </div>
            )}
          </div>
        )}

        {showLoginPopup && (
          <ClientLoginPopup show={showLoginPopup} onClose={closeLoginPopup} />
        )}

        {activeTab === "practice-areas" && (
          <div className="practice-areas-grid">
            {firm.practice_areas.map((area, index) => {
              const areaLawyers = lawyers.filter((lawyer) =>
                lawyer.practice_areas.some((pa) =>
                  pa.toLowerCase().includes(area.toLowerCase())
                )
              );

              return (
                <div key={index} className="practice-area-card">
                  <div className="practice-area-header">
                    <h3 className="practice-area-title">
                      {getPracticeAreaLabel(area)}
                    </h3>
                    <span
                      className={`lawyer-count-badge ${getPracticeAreaColor(
                        area
                      )}`}
                    >
                      {areaLawyers.length}{" "}
                      {areaLawyers.length === 1 ? "lawyer" : "lawyers"}
                    </span>
                  </div>

                  <div className="practice-area-lawyers">
                    {areaLawyers.slice(0, 3).map((lawyer) => (
                      <div key={lawyer.id} className="practice-area-lawyer">
                        {lawyer.profile_photo_url ? (
                          <img
                            src={lawyer.profile_photo_url}
                            alt={lawyer.first_name}
                            className="mini-lawyer-photo"
                          />
                        ) : (
                          <div className="mini-lawyer-photo-fallback">
                            {lawyer.first_name.charAt(0)}
                            {lawyer.last_name.charAt(0)}
                          </div>
                        )}
                        <div className="mini-lawyer-info">
                          <p className="mini-lawyer-name">{lawyer.name}</p>
                          <p className="mini-lawyer-role">{lawyer.role}</p>
                        </div>
                        <div className="mini-lawyer-rating">
                          <Star size={12} className="star-filled" />
                          <span>{lawyer.average_rating}</span>
                        </div>
                      </div>
                    ))}

                    {areaLawyers.length > 3 && (
                      <button
                        onClick={() => {
                          setSelectedPracticeArea(area);
                          setActiveTab("lawyers");
                        }}
                        className="view-all-button"
                      >
                        View all {areaLawyers.length} lawyers in{" "}
                        {getPracticeAreaLabel(area)}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FirmDetail;
